import { Pass } from "@/models/pass";
import { TicketTypeWithId } from "@/models/ticketType";
import {
  QueryDocumentSnapshot,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import _ from "lodash";
import { getTicketCatalogImage } from "../utils/fashion-event";

const passCollection = "passes";
const ticketTypesCollection = "ticketTypes";

const passMap = (doc: QueryDocumentSnapshot): Pass => {
  const data = doc.data() as Pass;
  data.id = doc.id;
  data.displayName =
    data.displayName ??
    data.title
      .split("-")
      .map((word) => _.capitalize(word))
      .join(" ");
  // Derive slug from title for consistent matching (e.g., "tribe-pass" -> "tribe")
  data.slug = data.slug ?? data.title?.toLowerCase().replace(/-pass$/, "");
  return data;
};

export async function getPasses(): Promise<Pass[]> {
  const passSnapshot = await getDocs(collection(db, passCollection));
  return passSnapshot.docs.map((doc) => passMap(doc));
}

export async function getPassByDocumentId(
  documentId: string
): Promise<Pass | undefined> {
  const passSnapshot = await getDoc(doc(db, passCollection, documentId));
  return passSnapshot.exists() ? passMap(passSnapshot) : undefined;
}

export async function addPass(pass: Pass): Promise<void> {
  const collectionRef = collection(db, passCollection);
  const { id, ...passData } = pass;
  await addDoc(collectionRef, passData);
}

/**
 * Get all ticket types from the ticketTypes collection
 * Used by the new checkout system
 * Returns empty array if collection is unreadable (e.g., permissions issue)
 */
export async function getTicketTypes(): Promise<TicketTypeWithId[]> {
  try {
    const snapshot = await getDocs(collection(db, ticketTypesCollection));
    return snapshot.docs.map((doc) => ({
      ...(doc.data() as TicketTypeWithId),
      id: doc.id,
    }));
  } catch (error) {
    console.warn("Could not read ticketTypes collection:", error);
    return [];
  }
}

/**
 * Get a ticket type by its slug (document ID)
 */
export async function getTicketTypeBySlug(
  slug: string
): Promise<TicketTypeWithId | undefined> {
  const docSnapshot = await getDoc(doc(db, ticketTypesCollection, slug));
  if (!docSnapshot.exists()) return undefined;
  return {
    ...(docSnapshot.data() as TicketTypeWithId),
    id: docSnapshot.id,
  };
}

/**
 * Convert a TicketType to a Pass-compatible object for display
 * This allows the frontend to display passes from both collection systems
 */
export function ticketTypeToPass(ticketType: TicketTypeWithId): Pass {
  return {
    id: ticketType.id,
    title: ticketType.name,
    displayName: ticketType.name,
    description: ticketType.description,
    type: "pass", // ticketTypes are always passes (not partners)
    access: ticketType.features,
    image: getTicketCatalogImage(ticketType.slug || ticketType.id),
    productId: ticketType.stripeProductId,
    priceId: "", // Will be determined at checkout
    programHighlights: [],
    isTechLuncheon: false,
    isVipDinner: ticketType.slug === "vip",
    isActive: ticketType.isActive,
    slug: ticketType.slug || ticketType.id, // For matching with userPass.ticketTypeSlug
  };
}

/**
 * Get all passes from both collections, unified for display
 * Returns passes from both the legacy 'passes' collection and the new 'ticketTypes' collection
 * Resilient to errors - will return whatever data is available
 */
export async function getPassesUnified(): Promise<Pass[]> {
  try {
    const [passes, ticketTypes] = await Promise.all([
      getPasses().catch((err) => {
        console.warn("Could not read passes collection:", err);
        return [] as Pass[];
      }),
      getTicketTypes(),
    ]);

    // Convert ticketTypes to Pass format
    const ticketTypesAsPasses = ticketTypes.map(ticketTypeToPass);

    // Merge, preferring passes collection if there's an ID conflict
    const passIds = new Set(passes.map((p) => p.id));
    const uniqueTicketTypePasses = ticketTypesAsPasses.filter(
      (tt) => !passIds.has(tt.id)
    );

    return [...passes, ...uniqueTicketTypePasses];
  } catch (error) {
    console.error("Error in getPassesUnified:", error);
    // Fallback to just passes collection
    try {
      return await getPasses();
    } catch {
      return [];
    }
  }
}
